<template>
  <div class="v2-profile-pic">
    <div class="v2-profile-pic__avatar" aria-hidden="true">
      <img
        v-if="previewUrl"
        :src="previewUrl"
        :alt="displayName"
        class="v2-profile-pic__img"
      />
      <span v-else class="v2-profile-pic__initials">{{ initials }}</span>
    </div>

    <div class="v2-profile-pic__info">
      <p class="v2-profile-pic__title">Foto de perfil</p>
      <p class="v2-profile-pic__hint">
        Sube una imagen para que tu perfil se vea más personal.
        Si no puedes visualizar la imagen intenta cerrando e iniciando sesión.
      </p>
      <div class="v2-profile-pic__actions">
        <BaseButton
          variant="primary"
          size="sm"
          :loading="uploading"
          :disabled="uploading || !hasNewImage"
          @click="onSave"
        >
          <template #icon-left>
            <v-icon size="16">mdi-content-save</v-icon>
          </template>
          Guardar
        </BaseButton>
        <BaseButton
          variant="secondary"
          size="sm"
          :disabled="uploading"
          @click="triggerFileInput"
        >
          <template #icon-left>
            <v-icon size="16">mdi-pencil-outline</v-icon>
          </template>
          {{ previewUrl ? "Cambiar imagen" : "Subir imagen" }}
        </BaseButton>
        <BaseButton
          v-if="hasNewImage"
          variant="ghost"
          size="sm"
          :disabled="uploading"
          @click="onCancel"
        >
          Cancelar
        </BaseButton>
      </div>
      <input
        ref="fileInput"
        type="file"
        accept=".png,.jpg,.jpeg"
        class="v2-profile-pic__file-input"
        @change="onFileSelected"
      />
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import { mapActions, mapMutations, mapState } from "vuex";
import BaseButton from "@/components/memberDashboardV2/ui/BaseButton.vue";

export default {
  name: "ProfilePictureUploader",
  components: { BaseButton },
  props: {
    user: { type: Object, default: null },
  },
  data() {
    return {
      uploading: false,
      pendingFile: null,
      previewObjectUrl: null,
    };
  },
  computed: {
    ...mapState("user", ["user"]),
    persistedPicture() {
      const v = this.user && this.user.profilePicture;
      if (!v || v === "NOT_ASSIGNED" || v === "N/D") return null;
      return v;
    },
    previewUrl() {
      if (this.previewObjectUrl) return this.previewObjectUrl;
      return this.persistedPicture;
    },
    hasNewImage() {
      return Boolean(this.pendingFile);
    },
    displayName() {
      if (!this.user) return "Miembro";
      return (
        [this.user.name, this.user.firstLastname].filter(Boolean).join(" ") ||
        this.user.company ||
        "Miembro"
      );
    },
    initials() {
      const name = this.displayName;
      return String(name)
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((s) => s[0].toUpperCase())
        .join("") || "?";
    },
  },
  beforeDestroy() {
    if (this.previewObjectUrl) {
      URL.revokeObjectURL(this.previewObjectUrl);
    }
  },
  methods: {
    ...mapMutations("user", ["setUserProfilePicture"]),
    ...mapMutations("notification", ["show"]),
    ...mapActions("user", ["fetchProfileData"]),
    triggerFileInput() {
      if (this.$refs.fileInput) this.$refs.fileInput.click();
    },
    onFileSelected(event) {
      const [file] = event.target.files || [];
      if (!file) return;
      if (this.previewObjectUrl) {
        URL.revokeObjectURL(this.previewObjectUrl);
      }
      this.previewObjectUrl = URL.createObjectURL(file);
      this.pendingFile = file;
    },
    onCancel() {
      if (this.previewObjectUrl) {
        URL.revokeObjectURL(this.previewObjectUrl);
      }
      this.previewObjectUrl = null;
      this.pendingFile = null;
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
    },
    async onSave() {
      if (!this.pendingFile) return;
      const formData = new FormData();
      formData.append("file", this.pendingFile);
      this.uploading = true;
      try {
        const response = await Vue.axios.put(
          "/users/update-profile-picture",
          formData
        );
        const newUrl =
          (response.data && response.data.user && response.data.user.profilePicture) ||
          this.persistedPicture ||
          "";
        this.setUserProfilePicture(newUrl);
        this.onCancel();
        await this.fetchProfileData();
        this.show({
          text: "¡La imagen se ha actualizado correctamente!",
          color: "success",
        });
      } catch (error) {
        this.show({
          text:
            (error && error.response && error.response.data &&
              (error.response.data.error?.message || error.response.data.message)) ||
            "No se pudo actualizar la imagen",
          color: "error",
        });
      } finally {
        this.uploading = false;
      }
    },
  },
};
</script>

<style scoped>
.v2-profile-pic {
  display: flex;
  align-items: center;
  gap: var(--space-4);
  padding: var(--space-4);
  background-color: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  flex-wrap: wrap;
}

.v2-profile-pic__avatar {
  width: 96px;
  height: 96px;
  border-radius: var(--radius-full);
  background-color: var(--color-neutral-100);
  border: 3px solid var(--color-amber);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
  box-shadow: var(--shadow-sm);
}

.v2-profile-pic__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.v2-profile-pic__initials {
  font-family: var(--font-headline);
  font-size: var(--fs-display-md);
  font-weight: var(--fw-bold);
  color: var(--color-navy-deep);
  line-height: 1;
}

.v2-profile-pic__info {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  flex: 1 1 240px;
  min-width: 0;
}

.v2-profile-pic__title {
  margin: 0;
  font-family: var(--font-body);
  font-size: var(--fs-body-md);
  font-weight: var(--fw-semibold);
  color: var(--color-text);
}

.v2-profile-pic__hint {
  margin: 0;
  font-size: var(--fs-body-sm);
  color: var(--color-text-muted);
  line-height: var(--lh-snug);
}

.v2-profile-pic__actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
}

.v2-profile-pic__file-input {
  display: none;
}
</style>